import React from 'react';

import type { ContentReferenceContext as ContentReferenceContextType } from '@gitbook/app-database';

const ContentReferenceReactContext = React.createContext<ContentReferenceContextType | null>(null);

/**
 * A provider for ContentReferenceContext.
 *
 * We use a named component here instead of allowing React.useContext
 * just to avoid bad names like ContentReferenceContextContext, and
 * to provide memoization.
 */
export function ContentReferenceContextProvider(props: {
    children: React.ReactNode;
    value: ContentReferenceContextType;
}) {
    const { value, children } = props;
    const organization = value.organization;
    const space = value.space;
    const page = value.page;
    const changeRequest = 'changeRequest' in value ? value.changeRequest : undefined;
    const revision = 'revision' in value ? value.revision : undefined;
    const hiveBranch = 'hiveBranch' in value ? value.hiveBranch : undefined;
    const reusableContent = 'reusableContent' in value ? value.reusableContent : undefined;
    const site = value.site;

    const memoized = React.useMemo<ContentReferenceContextType>(
        () => ({
            organization,
            space,
            page,
            site,
            ...(changeRequest ? { changeRequest } : {}),
            ...(revision ? { revision } : {}),
            ...(hiveBranch ? { hiveBranch } : {}),
            ...(reusableContent ? { reusableContent } : {}),
        }),
        [organization, space, page, changeRequest, revision, hiveBranch, site, reusableContent]
    );

    return (
        <ContentReferenceReactContext.Provider value={memoized}>
            {children}
        </ContentReferenceReactContext.Provider>
    );
}

/**
 * Use the current ContentReferenceContext. Throws an error if none is defined.
 */
export function useContentReferenceContext(): ContentReferenceContextType {
    const value = React.useContext(ContentReferenceReactContext);

    if (value === null) {
        throw new Error(
            'useContentReferenceContext expects a ContentReferenceContextProvider above it'
        );
    }

    return value;
}
